import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Loader2, Play, X, ChevronLeft, ChevronRight, GripVertical } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import SelectField from "../../components/os/SelectField";
import { input, GrHeader, GrModal } from "./_grShared";

const SLIDE_TYPES = [{ value: "portada", label: "Portada" }, { value: "recorrido", label: "Recorrido 3D (URL)" }, { value: "render", label: "Render / Imagen (URL)" }, { value: "video", label: "Video (URL)" }, { value: "texto", label: "Texto / Detalles" }, { value: "unidad", label: "Unidad / Precio" }];

export default function GrSalesPresentation() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [nf, setNf] = useState({ client: "", property: "" });
  const [edit, setEdit] = useState(null);
  const [present, setPresent] = useState(null);
  const [pIdx, setPIdx] = useState(0);
  const [slide, setSlide] = useState({ type: "recorrido", title: "", url: "" });

  const load = () => api.get("/gr/presentations").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!nf.client.trim()) return toast.error("Cliente requerido");
    try { const r = await api.post("/gr/presentations", { ...nf, slides: [], result: "borrador" }); setNewOpen(false); setNf({ client: "", property: "" }); load(); setEdit(r.data); toast.success("Presentación creada"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const saveSlides = async (pres, slides) => { setEdit({ ...pres, slides }); try { await api.patch(`/gr/presentations/${pres.id}`, { slides }); setItems((p) => p.map((x) => x.id === pres.id ? { ...x, slides } : x)); } catch { toast.error("Error"); } };
  const addSlide = () => { if (!slide.title.trim()) return toast.error("Título de slide requerido"); saveSlides(edit, [...(edit.slides || []), { id: Date.now().toString(), ...slide }]); setSlide({ type: "recorrido", title: "", url: "" }); };
  const removeSlide = (sid) => saveSlides(edit, edit.slides.filter((s) => s.id !== sid));
  const move = (i, d) => { const s = [...edit.slides]; const j = i + d; if (j < 0 || j >= s.length) return; [s[i], s[j]] = [s[j], s[i]]; saveSlides(edit, s); };
  const del = async (id) => { try { await api.delete(`/gr/presentations/${id}`); load(); } catch { /* */ } };

  const startPresent = (p) => { if (!p.slides?.length) return toast.error("Agrega slides primero"); setPresent(p); setPIdx(0); };

  return (
    <OSLayout title="OMNIA Sales Presentation">
      <div className="space-y-6" data-testid="gr-presentation-page">
        <div className="flex items-end justify-between gap-3">
          <GrHeader title="OMNIA Sales Presentation" desc="Arma presentaciones comerciales que integran recorridos, renders, videos y unidades en un solo flujo." />
          <button onClick={() => setNewOpen(true)} className="btn btn-primary shrink-0" data-testid="gr-pres-new"><Plus className="w-4 h-4" /> Nueva presentación</button>
        </div>

        {loading ? <div className="py-12 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> :
          items.length === 0 ? <div className="card-surface py-12 text-center text-navy/40 text-sm">Sin presentaciones.</div> :
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gr-pres-grid">
            {items.map((p) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card-surface p-5" data-testid={`gr-pres-${p.id}`}>
                <div className="flex items-start justify-between"><div><div className="font-medium">{p.client}</div><div className="text-xs text-navy/50">{p.property || "—"} · {p.slides?.length || 0} slides</div></div><button onClick={() => del(p.id)} className="text-navy/25 hover:text-destructive"><Trash2 className="w-4 h-4" /></button></div>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => setEdit(p)} className="btn btn-outline text-sm flex-1" data-testid={`gr-pres-edit-${p.id}`}>Editar</button>
                  <button onClick={() => startPresent(p)} className="btn btn-primary text-sm flex-1" data-testid={`gr-pres-play-${p.id}`}><Play className="w-3.5 h-3.5" /> Presentar</button>
                </div>
              </motion.div>
            ))}
          </div>}
      </div>

      {newOpen && <GrModal title="Nueva presentación" onClose={() => setNewOpen(false)} testid="gr-pres-dialog">
        <div className="space-y-3">
          <input className={input} placeholder="Cliente" value={nf.client} onChange={(e) => setNf({ ...nf, client: e.target.value })} data-testid="gr-pres-client" />
          <input className={input} placeholder="Propiedad / Desarrollo" value={nf.property} onChange={(e) => setNf({ ...nf, property: e.target.value })} />
        </div>
        <div className="flex justify-end gap-2 mt-5"><button onClick={() => setNewOpen(false)} className="btn btn-ghost">Cancelar</button><button onClick={create} className="btn btn-primary" data-testid="gr-pres-submit">Crear</button></div>
      </GrModal>}

      {edit && <GrModal title={`Editar: ${edit.client}`} onClose={() => setEdit(null)} testid="gr-pres-editor" wide>
        <div className="space-y-3">
          <div className="space-y-2" data-testid="gr-pres-slides">
            {(edit.slides || []).map((s, i) => (
              <div key={s.id} className="flex items-center gap-2 border border-navy/10 rounded-lg px-3 py-2 text-sm">
                <GripVertical className="w-4 h-4 text-navy/20" />
                <span className="badge badge-violet">{SLIDE_TYPES.find((t) => t.value === s.type)?.label}</span>
                <span className="flex-1 truncate">{s.title}</span>
                <button onClick={() => move(i, -1)} className="text-navy/30 hover:text-navy"><ChevronLeft className="w-4 h-4 rotate-90" /></button>
                <button onClick={() => move(i, 1)} className="text-navy/30 hover:text-navy"><ChevronRight className="w-4 h-4 rotate-90" /></button>
                <button onClick={() => removeSlide(s.id)} className="text-navy/25 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {(edit.slides || []).length === 0 && <div className="text-xs text-navy/40">Agrega tu primer slide.</div>}
          </div>
          <div className="border-t border-navy/10 pt-3 space-y-2">
            <SelectField value={slide.type} options={SLIDE_TYPES} onChange={(v) => setSlide({ ...slide, type: v })} />
            <input className={input} placeholder="Título del slide" value={slide.title} onChange={(e) => setSlide({ ...slide, title: e.target.value })} data-testid="gr-slide-title" />
            <input className={input} placeholder="URL / contenido (según tipo)" value={slide.url} onChange={(e) => setSlide({ ...slide, url: e.target.value })} />
            <button onClick={addSlide} className="btn btn-outline w-full text-sm" data-testid="gr-slide-add"><Plus className="w-4 h-4" /> Agregar slide</button>
          </div>
          <button onClick={() => { setEdit(null); startPresent(edit); }} className="btn btn-primary w-full"><Play className="w-4 h-4" /> Presentar ahora</button>
        </div>
      </GrModal>}

      {present && (
        <div className="fixed inset-0 z-[60] bg-navy flex flex-col" data-testid="gr-pres-play-mode">
          <div className="flex items-center justify-between px-6 py-3 text-white/70">
            <span className="text-sm">{present.client} · {pIdx + 1}/{present.slides.length}</span>
            <button onClick={() => setPresent(null)}><X className="w-6 h-6" /></button>
          </div>
          <div className="flex-1 flex items-center justify-center p-6">
            <Slide s={present.slides[pIdx]} />
          </div>
          <div className="flex items-center justify-between px-6 py-4">
            <button onClick={() => setPIdx((i) => Math.max(0, i - 1))} disabled={pIdx === 0} className="btn btn-ghost text-white disabled:opacity-30"><ChevronLeft className="w-5 h-5" /> Anterior</button>
            <button onClick={() => setPIdx((i) => Math.min(present.slides.length - 1, i + 1))} disabled={pIdx === present.slides.length - 1} className="btn btn-primary disabled:opacity-30" data-testid="gr-pres-next">Siguiente <ChevronRight className="w-5 h-5" /></button>
          </div>
        </div>
      )}
    </OSLayout>
  );
}

function Slide({ s }) {
  if (!s) return null;
  if (["recorrido", "video"].includes(s.type) && s.url)
    return <div className="w-full max-w-4xl aspect-video bg-black rounded-lg overflow-hidden"><iframe title={s.title} src={s.url} className="w-full h-full" allowFullScreen frameBorder="0" /></div>;
  if (s.type === "render" && s.url)
    return <img src={s.url} alt={s.title} className="max-h-[70vh] max-w-full rounded-lg object-contain" />;
  return (
    <div className="text-center text-white max-w-2xl">
      <div className="overline text-violet-300 mb-3">{SLIDE_TYPES.find((t) => t.value === s.type)?.label}</div>
      <h2 className="font-editorial text-5xl mb-4">{s.title}</h2>
      {s.url && <p className="text-white/70 whitespace-pre-wrap">{s.url}</p>}
    </div>
  );
}
